import {
  BMButton,
  BMComponentsType,
  BMInputField,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import {
  ComponentRef,
  MutableRefObject,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { FlatList, Image, Pressable, StyleSheet } from "react-native";

import { LOGO_BASE64 } from "../../../assets/logo";
import BMView from "../../../components/View/BMView";
import { useMobileContentResource } from "../../../core/resource/MobileResourceProvider";
import useSafeCountdown from "../../../hooks/useSafeCountdown";
import { formatMessage } from "../../../utils";

export type RefOtpComponent = {
  startCountdown(sec: number): void;
};

type Props = {
  email: string;
  ref?: MutableRefObject<RefOtpComponent | undefined>;
  errorMessage?: string;
  isLoading?: boolean;
  onSubmit: (otp: string) => void;
  onResendOtp?: () => Promise<boolean>;
  onClickLogo?: () => void;
};

const OTP_LENGTH = 6;
const START_OTP_TIMER = 30;

export default function MultiFactorOtp({
  ref,
  email,
  errorMessage,
  isLoading,
  onSubmit,
  onResendOtp,
  onClickLogo,
}: Props) {
  const { BMComponents } = useBMTheme();
  const { CorporateEmailOtp: cr } = useMobileContentResource();
  const inputRefs = useRef<Array<ComponentRef<typeof BMInputField> | null>>([]);

  const [otp, setOtp] = useState<string[]>(new Array(OTP_LENGTH).fill(""));
  const { countdown, startCountdown } = useSafeCountdown(START_OTP_TIMER);

  const styles = createStyles(BMComponents);

  useImperativeHandle(ref, () => ({
    startCountdown,
  }));

  function handleOnChangeText(text: string, index: number) {
    if (isNaN(+text) || otp.filter(Boolean).length === OTP_LENGTH) return;

    setOtp((prevOtp) => {
      const newOtp = [...prevOtp];
      if (text.length === 1) {
        // handle case where user types a single character
        newOtp[index] = text;
        inputRefs.current[index + 1]?.focus();
      } else if (text.length > 1) {
        // handle case where user pastes multiple characters
        const indexLeft = Math.min(text.length, OTP_LENGTH) - index;
        Array.from({ length: indexLeft }).forEach((_, newValueIndex) => {
          newOtp[index + newValueIndex] = text[newValueIndex];
        });
        inputRefs.current[indexLeft + index - 1]?.focus();
      }
      return newOtp;
    });
  }

  function handleKeyDown(index: number, e: any) {
    if (e.key === "Backspace") {
      setOtp((prev) => {
        const newOtp = [...prev];
        const currentValue = prev[index];

        if (currentValue) {
          newOtp[index] = "";
        } else if (index > 0) {
          newOtp[index - 1] = "";
          inputRefs.current[index - 1]?.focus();
        }

        return newOtp;
      });
    }
  }

  async function handleOnResendOtp() {
    onResendOtp?.()
      .then((success) => {
        if (success) {
          startCountdown(START_OTP_TIMER);
          return;
        }
        startCountdown(0);
      })
      .catch(() => {
        startCountdown(0);
      });
  }

  useEffect(() => {
    if (otp.every(Boolean)) {
      onSubmit(otp.join(""));
    }
  }, [otp]);

  return (
    <BMView style={styles.wrapper} align="center" spacing="xl">
      <BMView align="center" spacing="xl4">
        <Pressable onPress={onClickLogo}>
          <Image style={styles.logo} source={{ uri: LOGO_BASE64 }} />
        </Pressable>
        <BMText variant="titleNormal" ink="BMStyleTextInkPrimary">
          {cr.title}
        </BMText>
      </BMView>
      <BMView flex align="center" spacing="lg">
        <BMText
          style={styles.textAlignCenter}
          variant="paragraphNormal"
          ink="BMStyleTextInkSecondary"
        >
          {formatMessage(cr.description, {
            email: (
              <BMText
                variant="paragraphNormalEm"
                ink="BMStyleTextInkSecondary"
                key="email"
              >
                {email}
              </BMText>
            ),
          })}
        </BMText>
        <BMView>
          <FlatList
            data={otp}
            horizontal
            keyExtractor={(_, index) => index.toString()}
            contentContainerStyle={styles.inputContainer}
            scrollEnabled={false}
            renderItem={({ item, index }) => (
              <BMInputField
                label=""
                ref={(ref) => {
                  inputRefs.current[index] = ref;
                }}
                autoFocus={index === 0}
                key={index}
                value={item}
                onChangeText={(text) => handleOnChangeText(text, index)}
                onKeyPress={(e) => handleKeyDown(index, e)}
                placeholder="_"
                style={styles.input}
                keyboardType="numeric"
              />
            )}
          />
          {errorMessage ? (
            <BMText
              style={styles.textAlignCenter}
              variant="uiSmall"
              ink="BMStyleTextInkHighlightRed"
            >
              {errorMessage}
            </BMText>
          ) : null}
        </BMView>
        <BMView flex align="center" spacing="lg" style={styles.w100}>
          {countdown.sec > 0 || isLoading ? (
            <BMText
              style={[styles.countdown, styles.textAlignCenter]}
              ink="BMStyleTextInkMuted"
              variant="uiHuge"
            >
              {countdown.sec > 0
                ? formatMessage(cr.btnWaitResendCode, {
                    sec: Math.max(countdown.sec, 0),
                  })
                : cr.btnResendCode}
            </BMText>
          ) : (
            <BMButton
              label={cr.btnResendCode}
              size="LARGE"
              stretchable
              onPress={handleOnResendOtp}
              buttonStyle={styles.textButton}
            />
          )}
          <BMButton
            label={cr.btnVerify}
            role="PRIMARY"
            size="LARGE"
            stretchable
            onPress={() => onSubmit(otp.join(""))}
            disabled={isLoading || otp.filter(Boolean).length !== OTP_LENGTH}
            loading={isLoading}
          />
        </BMView>
      </BMView>
    </BMView>
  );
}

const createStyles = (BMComponents: BMComponentsType) => {
  return StyleSheet.create({
    logo: {
      height: 28,
      width: 280,
      resizeMode: "contain",
    },
    wrapper: {
      paddingHorizontal: BMPrimitives.BMSize.lg,
    },
    inputContainer: {
      flex: 1,
      justifyContent: "center",
      gap: BMPrimitives.BMSize.xs,
      marginHorizontal: BMPrimitives.BMSize.sm,
      marginVertical: BMPrimitives.BMSize.sm,
    },
    input: {
      width: 40,
      height: 40,
      paddingLeft: 17,
      marginLeft: -12,
      marginRight: -16,
      marginBottom: 1,
      outlineStyle: "none",
    },
    countdown: {
      marginVertical: 14,
    },
    textButton: {
      borderColor: "transparent",
      backgroundColor: "transparent",
      activeBackgroundColor: "transparent",
      color: BMComponents.BMStyleTextInkAction,
    },
    textAlignCenter: {
      textAlign: "center",
    },
    w100: {
      width: "100%",
    },
  });
};
